import { zodResolver } from "@hookform/resolvers/zod";
import { Check, PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { Control, Path, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { Column, LocationInput, Row } from "@/components";
import { FormDivider } from "@/components/form/FormDivider";
import { FormSection } from "@/components/form/FormSection";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { desiredPositionOptions } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CampaignResponse, CreateOrUpdateCampaignRequest } from "@/types";

// Internal phone number options for campaigns. They don't change, so we can hardcode them.
const internalPhoneNumberOptions = [
  {
    id: "e2bb8bc9-b4ed-46bf-a936-62033fdbecda",
    phone: "18659108710",
    provider: "TWILIO",
    description: "Latest Twilio A2P phone number",
  },
  {
    id: "eb4b91f2-3d93-427c-b9d8-c6b3b059a54a",
    phone: "18447230500",
    provider: "TWILIO",
    description: "Our second Twilio toll-free number",
  },
];

const locationAndRadiusSchema = z.object({
  location: z.string().min(1, "Required"),
  radius: z.coerce.number().min(1, "Required"),
});

const campaignFormSchema = z.object({
  name: z.string().min(1, "Required"),
  internalPhoneNumberId: z.string().min(1, "Required"),
  sentAt: z.string().optional(),
  payload: z.object({
    content: z.string().min(1, "Required"),
    desiredPositionSelections: z.array(z.string()),
    shouldTargetNullDesiredPosition: z.boolean(),
    shouldTargetUnregisteredUsers: z.boolean(),
    locationAndRadiusSelections: z.array(locationAndRadiusSchema),
    campaignTagIncludeSelections: z.array(z.string()),
    campaignTagExcludeSelections: z.array(z.string()),
    phoneNumbersToExclude: z.string().optional(),
    phoneNumbersToInclude: z.string().optional(),
  }),
});

type CampaignFormData = z.infer<typeof campaignFormSchema>;

const LocationAndRadiusInput = ({
  control,
  index,
  onRemove,
}: {
  control: Control<CampaignFormData>;
  index: number;
  onRemove: () => void;
}) => {
  const locationFieldName =
    `payload.locationAndRadiusSelections.${index.toString()}.location` as Path<CampaignFormData>;

  return (
    <Row className="items-start gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <LocationInput fieldName={locationFieldName} formControl={control} />
      <FormField
        control={control}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        name={`payload.locationAndRadiusSelections.${index}.radius`}
        render={({ field }) => (
          <FormItem className="w-32 text-slate-700">
            <FormLabel>Radius (Miles)</FormLabel>
            <FormControl>
              <Input placeholder="30" type="number" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button className="mt-8" onClick={onRemove} type="button" variant="ghost">
        <TrashIcon className="h-4 w-4" />
      </Button>
    </Row>
  );
};

export const CampaignsForm = ({
  campaign,
  isSubmitting,
  onSubmit,
  submitButtonText,
}: {
  campaign?: CampaignResponse;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrUpdateCampaignRequest) => void;
  submitButtonText: string;
}) => {
  const [reachEstimate, setReachEstimate] = useState<number | null>(null);
  const [reachEstimateError, setReachEstimateError] = useState<string | null>(
    null,
  );

  // TODO: Potentially better mechanism for fetching campaign tags.
  const { data: campaignTags = [] } =
    novaClient.internal.admin.campaigns.findManyTags.useQuery({});

  const { mutate: estimateReach, isPending: isEstimatingReach } =
    novaClient.internal.admin.campaigns.estimateReach.useMutation({
      onSuccess: (data) => {
        setReachEstimate(data);
        setReachEstimateError(null);
        mixpanelUtils.trackButtonClick(
          "Estimate reach",
          MIXPANEL_SCREENS.CAMPAIGNS,
          { estimated_reach_result: data },
        );
      },
      onError: (error) => {
        setReachEstimateError(
          error instanceof Error ? error.message : "Failed to estimate reach.",
        );
        setReachEstimate(null);
      },
    });

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: campaign?.campaign.name ?? "",
      internalPhoneNumberId: campaign?.campaign.internalPhoneNumberId ?? "",
      sentAt: campaign?.campaign.sentAt ?? "",
      payload: {
        content: campaign?.campaign.payload.content ?? "",
        desiredPositionSelections:
          campaign?.campaign.payload.desiredPositionSelections ?? [],
        shouldTargetNullDesiredPosition:
          campaign?.campaign.payload.shouldTargetNullDesiredPosition ?? false,
        shouldTargetUnregisteredUsers:
          campaign?.campaign.payload.shouldTargetUnregisteredUsers ?? false,
        locationAndRadiusSelections:
          campaign?.campaign.payload.locationAndRadiusSelections ?? [],
        campaignTagIncludeSelections:
          campaign?.campaign.payload.campaignTagIncludeSelections ?? [],
        campaignTagExcludeSelections:
          campaign?.campaign.payload.campaignTagExcludeSelections ?? [],
        phoneNumbersToExclude:
          campaign?.campaign.payload.phoneNumbersToExclude ?? "",
        phoneNumbersToInclude:
          campaign?.campaign.payload.phoneNumbersToInclude ?? "",
      },
    },
  });

  const {
    append: appendLocation,
    fields: locationFields,
    remove: removeLocation,
  } = useFieldArray({
    control: form.control,
    name: "payload.locationAndRadiusSelections",
  });

  const handleSubmit = (data: CampaignFormData) => {
    mixpanelUtils.trackFormSubmission(
      "Campaign submit",
      MIXPANEL_SCREENS.CAMPAIGNS,
      {
        has_sent_at: !!data.sentAt,
        location_count: data.payload.locationAndRadiusSelections.length,
      },
    );
    onSubmit({
      name: data.name,
      internalPhoneNumberId: data.internalPhoneNumberId,
      ...(data.sentAt && { sentAt: new Date(data.sentAt).toISOString() }),
      payload: {
        content: data.payload.content,
        desiredPositionSelections: data.payload.desiredPositionSelections,
        shouldTargetNullDesiredPosition:
          data.payload.shouldTargetNullDesiredPosition,
        shouldTargetUnregisteredUsers: data.payload.shouldTargetUnregisteredUsers,
        locationAndRadiusSelections: data.payload.locationAndRadiusSelections,
        campaignTagIncludeSelections: data.payload.campaignTagIncludeSelections,
        campaignTagExcludeSelections: data.payload.campaignTagExcludeSelections,
        ...(data.payload.phoneNumbersToExclude && {
          phoneNumbersToExclude: data.payload.phoneNumbersToExclude,
        }),
        ...(data.payload.phoneNumbersToInclude && {
          phoneNumbersToInclude: data.payload.phoneNumbersToInclude,
        }),
      },
    });
  };

  const handleEstimateReach = () => {
    const formData = form.getValues();
    // Parse through schema to ensure correct form data.
    // Not perfect because certain fields aren't required
    // for the reach estimate but it works for now.
    const parsed = campaignFormSchema.safeParse(formData);
    if (!parsed.success) {
      setReachEstimateError(
        "Please ensure the form is complete before estimating reach.",
      );
      setReachEstimate(null);
      return;
    }
    // Clear any previous errors
    setReachEstimateError(null);
    estimateReach({
      desiredPositionSelections: parsed.data.payload.desiredPositionSelections,
      shouldTargetNullDesiredPosition:
        parsed.data.payload.shouldTargetNullDesiredPosition,
      shouldTargetUnregisteredUsers: parsed.data.payload.shouldTargetUnregisteredUsers,
      locationAndRadiusSelections:
        parsed.data.payload.locationAndRadiusSelections,
      campaignTagIncludeSelections:
        parsed.data.payload.campaignTagIncludeSelections,
      campaignTagExcludeSelections:
        parsed.data.payload.campaignTagExcludeSelections,
      ...(parsed.data.payload.phoneNumbersToExclude && {
        phoneNumbersToExclude: parsed.data.payload.phoneNumbersToExclude,
      }),
      ...(parsed.data.payload.phoneNumbersToInclude && {
        phoneNumbersToInclude: parsed.data.payload.phoneNumbersToInclude,
      }),
    });
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-10"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormSection
          title="1. Basic Information"
          description="Campaign name and configuration."
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Campaign name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="internalPhoneNumberId"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Internal Phone Number</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select phone number" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {internalPhoneNumberOptions.map((phone) => (
                      <SelectItem key={phone.id} value={phone.id}>
                        {phone.phone} ({phone.description})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sentAt"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Send At</FormLabel>
                <FormControl>
                  <Input type="datetime-local" {...field} />
                </FormControl>
                <FormDescription>
                  Schedule when the campaign should be sent. Leave blank to
                  create a campaign in draft mode.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormDivider />

        <FormSection
          title="2. Campaign Segmentation"
          description="Define the target audience for this campaign."
        >
          <FormField
            control={form.control}
            name="payload.desiredPositionSelections"
            render={({ field }) => {
              return (
                <FormItem className="text-slate-700">
                  <FormLabel>Desired Positions</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (!field.value.includes(value)) {
                        field.onChange([...field.value, value]);
                      }
                    }}
                    value=""
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Add a position" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-60 text-slate-700">
                      {desiredPositionOptions.map((role) => {
                        const isSelected = field.value.includes(role.value);
                        return (
                          <SelectItem key={role.value} value={role.value}>
                            <Row className="items-center gap-2">
                              {isSelected && (
                                <Check className="h-4 w-4 text-slate-600" />
                              )}
                              <span className={isSelected ? "font-medium" : ""}>
                                {role.label}
                              </span>
                            </Row>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {field.value.length > 0 && (
                    <Row className="mt-2 flex-wrap gap-2">
                      {field.value.map((position) => (
                        <span
                          key={position}
                          className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-2 py-1 text-sm text-slate-700"
                        >
                          {position}
                          {/* TODO: Replace with a more proper button component. */}
                          <button
                            className="text-slate-500 hover:text-slate-700"
                            onClick={() => {
                              mixpanelUtils.trackButtonClick(
                                "Remove position",
                                MIXPANEL_SCREENS.CAMPAIGNS,
                              );
                              field.onChange(
                                field.value.filter((v) => v !== position),
                              );
                            }}
                            type="button"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </Row>
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="payload.shouldTargetNullDesiredPosition"
            render={({ field }) => (
              <FormItem className="flex items-start gap-3 space-y-0 text-slate-700">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <Column className="gap-1 leading-none">
                  <FormLabel>Should Target Null Desired Position</FormLabel>
                  <FormDescription>
                    This can be combined with non-null desired positions.
                  </FormDescription>
                </Column>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="payload.shouldTargetUnregisteredUsers"
            render={({ field }) => (
              <FormItem className="flex items-start gap-3 space-y-0 text-slate-700">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <Column className="gap-1 leading-none">
                  <FormLabel>Should Target Unregistered Users</FormLabel>
                  <FormDescription>
                    If true, the campaign will ONLY target unregistered users.
                  </FormDescription>
                </Column>
              </FormItem>
            )}
          />
          <Column className="gap-4">
            <Row className="items-center justify-between">
              <div>
                <FormLabel className="text-slate-700">Locations</FormLabel>
                <p className="text-sm text-slate-500">
                  Add locations with radius to target specific areas.
                </p>
              </div>
              <Button
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Add location",
                    MIXPANEL_SCREENS.CAMPAIGNS,
                  );
                  appendLocation({ location: "", radius: 30 });
                }}
                type="button"
                variant="outline"
              >
                <PlusIcon className="h-4 w-4" />
                Add location
              </Button>
            </Row>
            {locationFields.map((field, index) => (
              <LocationAndRadiusInput
                key={field.id}
                control={form.control}
                index={index}
                onRemove={() => {
                  mixpanelUtils.trackButtonClick(
                    "Remove location",
                    MIXPANEL_SCREENS.CAMPAIGNS,
                  );
                  removeLocation(index);
                }}
              />
            ))}
          </Column>
          {/* TODO: In the future we might want to make this dropdown searchable. */}
          <FormField
            control={form.control}
            name="payload.campaignTagIncludeSelections"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Campaign Tags Includes</FormLabel>
                <Select
                  onValueChange={(value) => {
                    if (!field.value.includes(value)) {
                      field.onChange([...field.value, value]);
                    }
                  }}
                  value=""
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Add a tag to include" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {campaignTags.map((tag) => {
                      const isSelected = field.value.includes(tag.id);
                      return (
                        <SelectItem key={tag.id} value={tag.id}>
                          <Row className="items-center gap-2">
                            {isSelected && (
                              <Check className="h-4 w-4 text-slate-600" />
                            )}
                            <span className={isSelected ? "font-medium" : ""}>
                              {tag.name}
                            </span>
                          </Row>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {field.value.length > 0 && (
                  <Row className="mt-2 flex-wrap gap-2">
                    {field.value.map((tagId) => {
                      const tag = campaignTags.find((t) => t.id === tagId);
                      return (
                        <span
                          key={tagId}
                          className="inline-flex items-center gap-2 rounded-md bg-green-100 px-2 py-1 text-sm text-green-700"
                        >
                          {tag?.name ?? tagId}
                          {/* TODO: Replace with a more proper button component. */}
                          <button
                            type="button"
                            className="text-green-500 hover:text-green-700"
                            onClick={() => {
                              field.onChange(
                                field.value.filter((v) => v !== tagId),
                              );
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      );
                    })}
                  </Row>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          {/* TODO: In the future we might want to make this dropdown searchable. */}
          <FormField
            control={form.control}
            name="payload.campaignTagExcludeSelections"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Campaign Tags Excludes</FormLabel>
                <Select
                  onValueChange={(value) => {
                    if (!field.value.includes(value)) {
                      field.onChange([...field.value, value]);
                    }
                  }}
                  value=""
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Add a tag to exclude" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {campaignTags.map((tag) => {
                      const isSelected = field.value.includes(tag.id);
                      return (
                        <SelectItem key={tag.id} value={tag.id}>
                          <Row className="items-center gap-2">
                            {isSelected && (
                              <Check className="h-4 w-4 text-slate-600" />
                            )}
                            <span className={isSelected ? "font-medium" : ""}>
                              {tag.name}
                            </span>
                          </Row>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {field.value.length > 0 && (
                  <Row className="mt-2 flex-wrap gap-2">
                    {field.value.map((tagId) => {
                      const tag = campaignTags.find((t) => t.id === tagId);
                      return (
                        <span
                          key={tagId}
                          className="inline-flex items-center gap-2 rounded-md bg-red-100 px-2 py-1 text-sm text-red-700"
                        >
                          {tag?.name ?? tagId}
                          {/* TODO: Replace with a more proper button component. */}
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              field.onChange(
                                field.value.filter((v) => v !== tagId),
                              );
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      );
                    })}
                  </Row>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="payload.phoneNumbersToInclude"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Phone Numbers to Include</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="e.g. 16507730201,14158764531"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Comma separated list of phone numbers to include in the
                  campaign.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="payload.phoneNumbersToExclude"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Phone Numbers to Exclude</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="e.g. 16507730201,14158764531"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Comma separated list of phone numbers to exclude from the
                  campaign.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormDivider />

        <FormSection
          title="3. Campaign Content"
          description="The message content that will be sent to users."
        >
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            Currently available dynamic variables:{" "}
            <span className="font-bold">&#123;firstName&#125;</span>,{" "}
            <span className="font-bold">&#123;location&#125;</span>, and{" "}
            <span className="font-bold">&#123;userReferralUrl&#125;</span>.
          </div>
          <FormField
            control={form.control}
            name="payload.content"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Content</FormLabel>
                <FormControl>
                  <Textarea
                    className="min-h-[200px]"
                    placeholder="Enter campaign message content..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <Row className="items-center justify-between gap-2">
          <Column className="gap-1">
            {reachEstimateError && (
              <span className="text-sm text-red-600">
                <span className="font-bold">Error:</span> {reachEstimateError}
              </span>
            )}
            {reachEstimate !== null && !reachEstimateError && (
              <>
                <span className="text-sm text-slate-700">
                  <strong>Estimated Reach:</strong>{" "}
                  {reachEstimate.toLocaleString()} users
                </span>
                <p className="text-xs text-slate-500">
                  Note: Estimated reach does not update automatically. Click
                  &quot;Estimate reach&quot; again to recalculate.
                </p>
              </>
            )}
          </Column>
          <Row className="items-center gap-2">
            <Button
              disabled={isSubmitting || isEstimatingReach}
              onClick={handleEstimateReach}
              type="button"
              variant="outline"
            >
              Estimate reach
            </Button>
            <Button disabled={isSubmitting} type="submit">
              {submitButtonText}
            </Button>
          </Row>
        </Row>
      </form>
    </Form>
  );
};
